/* Opal In Sky: cookie consent.
   CookieConsent v3 (https://cookieconsent.orestbida.com) as the UI, Shopify's Customer Privacy API
   as the source of truth.

   - Shopify decides where opt-in applies (admin: Settings → Customer privacy → Cookie banner → Regions).
     In those regions Shopify keeps analytics/marketing off, and its pixels paused, until
     setTrackingConsent() records a choice. The bar shows only when shouldShowBanner() is true and the
     visitor hasn't chosen yet.
   - The theme setting "Only show in these regions" can narrow that further (e.g. USCA, EEA, UK, CAQC). Visitors outside
     the list get no bar, and tracking stays off for them (it fails closed rather than assuming consent).
   - CookieConsent's own cookie is not trusted between visits: every page load starts from Shopify's
     recorded consent, so the two can never disagree.
   - window.oisConsent.showPreferences() re-opens the choices (used by the footer "Your privacy choices" link).
   - Outside the "Full notice regions" setting (default California, Europe, Quebec) the bar is a compact
     one-liner; the preferences panel is the same everywhere.
   - Add ?ois_consent_preview=1 to a URL to show the bar anywhere, for testing (=full or =compact forces a
     variant). Choices made there are recorded like any other. */
(() => {
  const configEl = document.getElementById('ois-consent-config');
  if (!configEl) return;

  /** @type {{js: string, css: string, regions: string[], fullRegions: string[], policyUrl: string, title: string, description: string}} */
  let config;
  try {
    config = JSON.parse(configEl.textContent || '{}');
  } catch {
    return;
  }

  const CC_COOKIE = 'ois_cc';
  // ?ois_consent_preview=1 shows the bar for this region; =full or =compact forces a variant.
  const preview = new URLSearchParams(location.search).get('ois_consent_preview');
  const gpc = /** @type {any} */ (navigator).globalPrivacyControl === true;

  /** Loads Shopify's Customer Privacy API. Resolves to null if the storefront doesn't provide it. */
  function loadPrivacyApi() {
    return new Promise((resolve) => {
      const shopify = /** @type {any} */ (window).Shopify;
      if (shopify?.customerPrivacy?.setTrackingConsent) return resolve(shopify.customerPrivacy);
      if (typeof shopify?.loadFeatures !== 'function') return resolve(null);
      const timer = setTimeout(() => resolve(null), 10000);
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (/** @type {any} */ error) => {
        clearTimeout(timer);
        resolve(error ? null : shopify.customerPrivacy || null);
      });
    });
  }

  /** @type {Promise<any> | undefined} */
  let libraryPromise;
  function loadLibrary() {
    libraryPromise ??= new Promise((resolve, reject) => {
      const win = /** @type {any} */ (window);
      if (win.CookieConsent) return resolve(win.CookieConsent);
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = config.css;
      document.head.append(link);
      const script = document.createElement('script');
      script.src = config.js;
      script.onload = () => (win.CookieConsent ? resolve(win.CookieConsent) : reject(new Error('CookieConsent missing')));
      script.onerror = reject;
      document.head.append(script);
    });
    return libraryPromise;
  }

  // Shortcuts for the "Only show in these regions" setting. Codes match the start of
  // getRegion() (ISO 3166-2 without the dash, e.g. USCA, CAQC, GBENG, DEBY).
  const EU = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];
  const REGION_GROUPS = {
    EU,
    EEA: [...EU, 'IS', 'LI', 'NO'],
    UK: ['GB'],
    EUROPE: [...EU, 'IS', 'LI', 'NO', 'GB', 'CH'],
    QUEBEC: ['CAQC'],
  };
  const expandRegions = (/** @type {string[] | undefined} */ codes) =>
    (codes || []).flatMap((code) => REGION_GROUPS[/** @type {keyof typeof REGION_GROUPS} */ (code)] ?? [code]);
  const matchesRegion = (/** @type {string[]} */ codes, /** @type {string} */ region) =>
    codes.some((code) => code && region.startsWith(code));

  const regionCodes = expandRegions(config.regions);
  const fullRegionCodes = expandRegions(config.fullRegions);

  function regionAllowed(/** @type {string} */ region) {
    return !regionCodes.length || matchesRegion(regionCodes, region);
  }

  /** Full notice where the strictest rules apply (default California, Europe, Quebec); a one-line bar elsewhere. */
  function useCompactBar(/** @type {string} */ region) {
    if (preview === 'compact') return true;
    if (preview === 'full' || !fullRegionCodes.length) return false;
    return !matchesRegion(fullRegionCodes, region);
  }

  function escapeHtml(/** @type {string} */ text) {
    return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }

  function policyLink(label = 'Privacy policy') {
    return config.policyUrl ? ` <a href="${escapeHtml(config.policyUrl)}">${label}</a>` : '';
  }


  const COPY = {
    en: {
      description:
        'We use cookies and similar tools to run the store, understand how it is used and measure our ads, ' +
        'including pixels from Google, Meta and TikTok that can share what you do here with those companies. ' +
        'Nothing beyond what the store needs to work runs until you choose.',
      policy: 'Privacy policy',
      acceptAll: 'Accept all',
      rejectAll: 'Reject all',
      manage: 'Manage preferences',
      save: 'Save my choices',
      close: 'Close',
      intro: 'Choose what you allow. You can change this any time from “Your privacy choices” in the footer.',
      gpc: ' Your browser sent a Global Privacy Control signal, so marketing starts off.',
      necessaryTitle: 'Strictly necessary',
      necessary: 'Cart, checkout, security and remembering these choices. Always on.',
      analyticsTitle: 'Analytics',
      analytics: 'Shopify and Google Analytics measure visits and what people look at, so we can improve the store.',
      marketingTitle: 'Marketing',
      marketing:
        'Pixels from Google, Meta (Facebook/Instagram), TikTok and Printful measure our ads and may share your ' +
        'activity with those companies. Turning this off also opts you out of the sale or sharing of your personal information.',
      preferencesTitle: 'Preferences',
      preferences: 'Remembers things like your country, language and currency.',
      moreTitle: 'More information',
      more: 'Questions about your data?',
      label: 'Cookie consent',
      short: 'We use cookies for analytics and ads (Google, Meta, TikTok) only if you agree.',
      acceptShort: 'Accept',
      rejectShort: 'Reject',
      manageShort: 'Settings',
    },
    fr: {
      title: 'Vos choix en matière de confidentialité',
      description:
        'Nous utilisons des témoins (cookies) et des outils semblables pour faire fonctionner la boutique, comprendre son utilisation ' +
        'et mesurer nos publicités, dont des pixels de Google, Meta et TikTok qui peuvent communiquer ce que vous faites ici à ces entreprises. ' +
        'Rien d’autre que ce qui est nécessaire au fonctionnement de la boutique n’est activé avant votre choix.',
      policy: 'Politique de confidentialité',
      acceptAll: 'Tout accepter',
      rejectAll: 'Tout refuser',
      manage: 'Gérer mes préférences',
      save: 'Enregistrer mes choix',
      close: 'Fermer',
      intro: 'Choisissez ce que vous autorisez. Vous pouvez modifier vos choix en tout temps grâce au lien « Your privacy choices » au bas de la page.',
      gpc: ' Votre navigateur a envoyé un signal Global Privacy Control : le marketing est donc désactivé par défaut.',
      necessaryTitle: 'Strictement nécessaires',
      necessary: 'Panier, paiement, sécurité et mémorisation de ces choix. Toujours actifs.',
      analyticsTitle: 'Statistiques',
      analytics: 'Shopify et Google Analytics mesurent les visites et les pages consultées afin d’améliorer la boutique.',
      marketingTitle: 'Marketing',
      marketing:
        'Des pixels de Google, Meta (Facebook/Instagram), TikTok et Printful mesurent nos publicités et peuvent communiquer votre ' +
        'activité à ces entreprises. Désactiver cette option vous retire aussi de la vente ou du partage de vos renseignements personnels.',
      preferencesTitle: 'Préférences',
      preferences: 'Mémorise, par exemple, votre pays, votre langue et votre devise.',
      moreTitle: 'Plus d’information',
      more: 'Des questions sur vos données?',
      label: 'Consentement aux témoins',
      short: 'Nous utilisons des témoins pour les statistiques et la publicité (Google, Meta, TikTok) seulement avec votre accord.',
      acceptShort: 'Accepter',
      rejectShort: 'Refuser',
      manageShort: 'Paramètres',
    },
  };

  /** CookieConsent translation for 'en' or 'fr'. The theme-editor title/message override English only. */
  function translation(/** @type {'en' | 'fr'} */ lang, compact = false) {
    const c = COPY[lang];
    const title = lang === 'en' ? config.title : COPY.fr.title;
    const description = (lang === 'en' && config.description) || c.description;
    const consentModal = compact
      ? {
          label: c.label,
          description: c.short + policyLink(c.policy),
          acceptAllBtn: c.acceptShort,
          acceptNecessaryBtn: c.rejectShort,
          showPreferencesBtn: c.manageShort,
        }
      : {
          title,
          description: description + policyLink(c.policy),
          acceptAllBtn: c.acceptAll,
          acceptNecessaryBtn: c.rejectAll,
          showPreferencesBtn: c.manage,
        };
    return {
      consentModal,
      preferencesModal: {
        title,
        acceptAllBtn: c.acceptAll,
        acceptNecessaryBtn: c.rejectAll,
        savePreferencesBtn: c.save,
        closeIconLabel: c.close,
        sections: [
          { description: c.intro + (gpc ? c.gpc : '') },
          { title: c.necessaryTitle, description: c.necessary, linkedCategory: 'necessary' },
          { title: c.analyticsTitle, description: c.analytics, linkedCategory: 'analytics' },
          { title: c.marketingTitle, description: c.marketing, linkedCategory: 'marketing' },
          { title: c.preferencesTitle, description: c.preferences, linkedCategory: 'preferences' },
          { title: c.moreTitle, description: c.more + policyLink(c.policy) },
        ],
      },
    };
  }

  /** @type {Promise<any> | undefined} */
  let ready;
  /** Runs CookieConsent once, seeded from Shopify's current consent. */
  function init(/** @type {any} */ privacy) {
    ready ??= loadLibrary().then((CookieConsent) => {
      const compact = useCompactBar(privacy.getRegion?.() || '');
      document.documentElement.classList.toggle('ois-cc-compact', compact);

      // Start from Shopify's record, not whatever CookieConsent remembered.
      document.cookie = `${CC_COOKIE}=; Max-Age=0; path=/; SameSite=Lax`;

      const pushToShopify = () => {
        const accepted = (/** @type {string} */ name) => CookieConsent.acceptedCategory(name);
        const marketing = accepted('marketing');
        privacy.setTrackingConsent(
          {
            analytics: accepted('analytics'),
            marketing,
            preferences: accepted('preferences'),
            // Declining marketing (or sending Global Privacy Control) also opts out of sale/sharing.
            sale_of_data: marketing && !gpc,
          },
          () => document.dispatchEvent(new CustomEvent('ois:consent-saved')),
        );
      };

      CookieConsent.run({
        mode: 'opt-in',
        autoShow: false,
        hideFromBots: true,
        disablePageInteraction: false,
        cookie: { name: CC_COOKIE, expiresAfterDays: 365, sameSite: 'Lax' },
        guiOptions: {
          consentModal: { layout: 'bar inline', position: 'bottom', equalWeightButtons: true, flipButtons: false },
          preferencesModal: { layout: 'box', equalWeightButtons: true, flipButtons: false },
        },
        categories: {
          necessary: { enabled: true, readOnly: true },
          analytics: { enabled: privacy.analyticsProcessingAllowed() },
          marketing: { enabled: privacy.marketingAllowed() && !gpc },
          preferences: { enabled: privacy.preferencesProcessingAllowed() },
        },
        onFirstConsent: pushToShopify,
        onChange: pushToShopify,
        language: {
          default: 'en',
          // French for visitors whose browser prefers it (Quebec's Bill 96 / Law 25); English otherwise.
          autoDetect: 'browser',
          translations: { en: translation('en', compact), fr: translation('fr', compact) },
        },
      });
      return CookieConsent;
    });
    return ready;
  }

  /** @type {Promise<any>} */
  const privacyReady = loadPrivacyApi();

  async function showPreferences() {
    const privacy = await privacyReady;
    if (!privacy) {
      if (config.policyUrl) location.href = config.policyUrl;
      return;
    }
    const CookieConsent = await init(privacy);
    CookieConsent.showPreferences();
  }

  /** @type {any} */ (window).oisConsent = { showPreferences };

  privacyReady.then(async (privacy) => {
    if (!privacy) return;
    // shouldShowBanner() only reports the region flag (it stays true after a choice), so also
    // require that no choice has been recorded yet.
    const consent = privacy.currentVisitorConsent?.() || {};
    const undecided = ['analytics', 'marketing', 'preferences'].some((key) => !consent[key]);
    const needed = preview !== null || (privacy.shouldShowBanner() && undecided && regionAllowed(privacy.getRegion() || ''));
    if (!needed) return;
    try {
      const CookieConsent = await init(privacy);
      CookieConsent.show(true);
    } catch (error) {
      console.error('[ois-consent]', error);
    }
  });
})();
